import type {
  LandingGlobusCampaignHeroContent,
  LandingLocale,
} from './landingContentTypes.js';
import { assets } from './landingContentShared.js';

interface LocalizedGlobusCampaignContent {
  applicant: LandingGlobusCampaignHeroContent;
  company: LandingGlobusCampaignHeroContent;
}

export const globusCampaignContent: Record<LandingLocale, LocalizedGlobusCampaignContent> = {
  de: {
    applicant: {
      title: 'Dein Job bei GLOBUS.',
      intro: 'Entdecke Jobs im Einzelhandel',
      image: assets.globusCampaignMarkthalle,
      imageAlt: 'GLOBUS Markthalle mit großem GLOBUS Schriftzug vor blauem Himmel',
      mediaCards: [
        { image: assets.globusCampaignGastronomy, imageAlt: 'Köchin bereitet in der GLOBUS Gastronomie frisches Gemüse zu' },
        { image: assets.globusCampaignFish, imageAlt: 'Mitarbeiterin präsentiert frischen Fisch an der GLOBUS Frischetheke' },
        { image: assets.globusCampaignButcherFeature, imageAlt: 'Metzgerin berät eine Familie an der GLOBUS Frischetheke' },
        { image: assets.globusCampaignBakeryFeature, imageAlt: 'Team der GLOBUS Meisterbäckerei vor der Backtheke' },
        { image: assets.globusCampaignMarkthalle, imageAlt: 'GLOBUS Markthalle mit großem GLOBUS Schriftzug vor blauem Himmel' },
      ],
      primaryAction: { label: 'Jobs entdecken', href: '/globus#globus-jobs-list' },
      secondaryAction: { label: 'GLOBUS kennenlernen', href: '/globus' },
      cards: [
        { title: 'Bäcker:in', image: assets.globusCampaignBakery, imageAlt: 'Bäcker formt in der GLOBUS Meisterbäckerei frischen Teig', href: '/globus#globus-job-bakery' },
        { title: 'Metzger:in', image: assets.globusCampaignButcher, imageAlt: 'Metzger begrüßt Kundinnen und Kunden in einer GLOBUS Markthalle', href: '/globus#globus-job-butcher' },
        { title: 'Frischetheke', image: assets.globusCampaignFish, imageAlt: 'Mitarbeiterin präsentiert frischen Fisch an der GLOBUS Frischetheke', href: '/globus#globus-job-fresh-counter' },
        { title: 'Gastronomie', image: assets.globusCampaignGastronomy, imageAlt: 'Köchin bereitet in der GLOBUS Gastronomie frisches Gemüse zu', href: '/globus#globus-job-gastronomy' },
      ],
    },
    company: {
      title: 'Fachkräfte für Frischeteams.',
      intro: 'So verbindet Viva Porta konkreten Personalbedarf mit vorqualifizierten internationalen Profilen.',
      image: assets.globusCampaignMarkthalle,
      imageAlt: 'GLOBUS Markthalle als Beispiel einer erfolgreichen Partnerschaft mit Viva Porta',
      mediaCards: [
        { image: assets.globusCampaignGastronomy, imageAlt: 'Fachkraft im direkten Kundenkontakt in der GLOBUS Gastronomie' },
        { image: assets.globusCampaignFish, imageAlt: 'Fachkraft präsentiert frischen Fisch an einer GLOBUS Frischetheke' },
        { image: assets.globusCampaignButcherFeature, imageAlt: 'Fachkraft berät eine Familie an der GLOBUS Frischetheke' },
        { image: assets.globusCampaignBakeryFeature, imageAlt: 'Team der GLOBUS Meisterbäckerei als Beispiel gelungener Personalgewinnung' },
        { image: assets.globusCampaignMarkthalle, imageAlt: 'GLOBUS Markthalle als Beispiel einer erfolgreichen Partnerschaft mit Viva Porta' },
      ],
      primaryAction: { label: 'Fachkräfte anfragen', href: '#fachkraefte-anfragen' },
      secondaryAction: { label: 'Partnerschaft ansehen', href: '/globus' },
      cards: [
        { title: 'Bäckerei', image: assets.globusCampaignBakery, imageAlt: 'Fachkraft arbeitet in der GLOBUS Meisterbäckerei', href: '/globus' },
        { title: 'Metzgerei', image: assets.globusCampaignButcher, imageAlt: 'Fachkraft arbeitet in der GLOBUS Metzgerei', href: '/globus' },
        { title: 'Frischetheke', image: assets.globusCampaignFish, imageAlt: 'Fachkraft arbeitet an einer GLOBUS Fischtheke', href: '/globus' },
        { title: 'Gastronomie', image: assets.globusCampaignGastronomy, imageAlt: 'Fachkraft arbeitet in der GLOBUS Gastronomie', href: '/globus' },
      ],
    },
  },
  en: {
    applicant: {
      title: 'Your job at GLOBUS.',
      intro: 'Discover roles in fresh food, skilled work and gastronomy. Viva Porta guides you personally.',
      image: assets.globusCampaignMarkthalle,
      imageAlt: 'GLOBUS market hall with the large GLOBUS sign against a blue sky',
      mediaCards: [
        { image: assets.globusCampaignGastronomy, imageAlt: 'Chef preparing fresh vegetables in GLOBUS gastronomy' },
        { image: assets.globusCampaignFish, imageAlt: 'Employee presenting fresh fish at a GLOBUS fresh-food counter' },
        { image: assets.globusCampaignButcherFeature, imageAlt: 'Butcher advising a family at the GLOBUS fresh-food counter' },
        { image: assets.globusCampaignBakeryFeature, imageAlt: 'GLOBUS master bakery team in front of the bakery counter' },
        { image: assets.globusCampaignMarkthalle, imageAlt: 'GLOBUS market hall with the large GLOBUS sign against a blue sky' },
      ],
      primaryAction: { label: 'Explore jobs', href: '/globus#globus-jobs-list' },
      secondaryAction: { label: 'Meet GLOBUS', href: '/globus' },
      cards: [
        { title: 'Baker', image: assets.globusCampaignBakery, imageAlt: 'Baker shaping fresh dough in the GLOBUS master bakery', href: '/globus#globus-job-bakery' },
        { title: 'Butcher', image: assets.globusCampaignButcher, imageAlt: 'Butcher welcoming customers in a GLOBUS market hall', href: '/globus#globus-job-butcher' },
        { title: 'Fresh-food counter', image: assets.globusCampaignFish, imageAlt: 'Employee presenting fresh fish at a GLOBUS fresh-food counter', href: '/globus#globus-job-fresh-counter' },
        { title: 'Gastronomy', image: assets.globusCampaignGastronomy, imageAlt: 'Chef preparing fresh vegetables in GLOBUS gastronomy', href: '/globus#globus-job-gastronomy' },
      ],
    },
    company: {
      title: 'Professionals for fresh-food teams.',
      intro: 'See how Viva Porta connects real staffing needs with pre-qualified international profiles.',
      image: assets.globusCampaignMarkthalle,
      imageAlt: 'GLOBUS market hall as an example of a successful partnership with Viva Porta',
      mediaCards: [
        { image: assets.globusCampaignGastronomy, imageAlt: 'Professional serving customers in GLOBUS gastronomy' },
        { image: assets.globusCampaignFish, imageAlt: 'Professional presenting fresh fish at a GLOBUS fresh-food counter' },
        { image: assets.globusCampaignButcherFeature, imageAlt: 'Professional advising a family at the GLOBUS fresh-food counter' },
        { image: assets.globusCampaignBakeryFeature, imageAlt: 'GLOBUS master bakery team as an example of successful recruitment' },
        { image: assets.globusCampaignMarkthalle, imageAlt: 'GLOBUS market hall as an example of a successful partnership with Viva Porta' },
      ],
      primaryAction: { label: 'Request professionals', href: '#fachkraefte-anfragen' },
      secondaryAction: { label: 'View partnership', href: '/globus' },
      cards: [
        { title: 'Bakery', image: assets.globusCampaignBakery, imageAlt: 'Professional working in the GLOBUS master bakery', href: '/globus' },
        { title: 'Butchery', image: assets.globusCampaignButcher, imageAlt: 'Professional working in GLOBUS butchery', href: '/globus' },
        { title: 'Fresh-food counter', image: assets.globusCampaignFish, imageAlt: 'Professional working at a GLOBUS fish counter', href: '/globus' },
        { title: 'Gastronomy', image: assets.globusCampaignGastronomy, imageAlt: 'Professional working in GLOBUS gastronomy', href: '/globus' },
      ],
    },
  },
  tr: {
    applicant: {
      title: 'GLOBUS’taki işin.',
      intro: 'Taze ürün, zanaat ve gastronomi alanlarındaki işleri keşfet. Viva Porta sana kişisel olarak eşlik eder.',
      image: assets.globusCampaignMarkthalle,
      imageAlt: 'Mavi gökyüzü önünde büyük GLOBUS tabelası bulunan GLOBUS marketi',
      mediaCards: [
        { image: assets.globusCampaignGastronomy, imageAlt: 'GLOBUS gastronomisinde taze sebze hazırlayan aşçı' },
        { image: assets.globusCampaignFish, imageAlt: 'GLOBUS taze ürün tezgâhında balık sunan çalışan' },
        { image: assets.globusCampaignButcherFeature, imageAlt: 'GLOBUS taze ürün tezgâhında bir aileye danışmanlık yapan kasap' },
        { image: assets.globusCampaignBakeryFeature, imageAlt: 'Fırın tezgâhının önünde GLOBUS usta fırın ekibi' },
        { image: assets.globusCampaignMarkthalle, imageAlt: 'Mavi gökyüzü önünde büyük GLOBUS tabelası bulunan GLOBUS marketi' },
      ],
      primaryAction: { label: 'İşleri keşfet', href: '/globus#globus-jobs-list' },
      secondaryAction: { label: 'GLOBUS’u tanı', href: '/globus' },
      cards: [
        { title: 'Fırıncı', image: assets.globusCampaignBakery, imageAlt: 'GLOBUS usta fırınında taze hamur şekillendiren fırıncı', href: '/globus#globus-job-bakery' },
        { title: 'Kasap', image: assets.globusCampaignButcher, imageAlt: 'GLOBUS marketinde müşterileri karşılayan kasap', href: '/globus#globus-job-butcher' },
        { title: 'Taze ürün tezgâhı', image: assets.globusCampaignFish, imageAlt: 'GLOBUS taze ürün tezgâhında balık sunan çalışan', href: '/globus#globus-job-fresh-counter' },
        { title: 'Gastronomi', image: assets.globusCampaignGastronomy, imageAlt: 'GLOBUS gastronomisinde taze sebze hazırlayan aşçı', href: '/globus#globus-job-gastronomy' },
      ],
    },
    company: {
      title: 'Taze ürün ekipleri için uzmanlar.',
      intro: 'Viva Porta’nın somut personel ihtiyacını önceden değerlendirilmiş uluslararası profillerle nasıl buluşturduğunu görün.',
      image: assets.globusCampaignMarkthalle,
      imageAlt: 'Viva Porta ile başarılı iş birliğine örnek olan GLOBUS marketi',
      mediaCards: [
        { image: assets.globusCampaignGastronomy, imageAlt: 'GLOBUS gastronomisinde müşterilerle doğrudan çalışan uzman' },
        { image: assets.globusCampaignFish, imageAlt: 'GLOBUS taze ürün tezgâhında balık sunan uzman' },
        { image: assets.globusCampaignButcherFeature, imageAlt: 'GLOBUS taze ürün tezgâhında bir aileye danışmanlık yapan uzman' },
        { image: assets.globusCampaignBakeryFeature, imageAlt: 'Başarılı personel kazanımına örnek olan GLOBUS usta fırın ekibi' },
        { image: assets.globusCampaignMarkthalle, imageAlt: 'Viva Porta ile başarılı iş birliğine örnek olan GLOBUS marketi' },
      ],
      primaryAction: { label: 'Uzman talep et', href: '#fachkraefte-anfragen' },
      secondaryAction: { label: 'İş birliğini incele', href: '/globus' },
      cards: [
        { title: 'Fırın', image: assets.globusCampaignBakery, imageAlt: 'GLOBUS usta fırınında çalışan uzman', href: '/globus' },
        { title: 'Kasap', image: assets.globusCampaignButcher, imageAlt: 'GLOBUS kasabında çalışan uzman', href: '/globus' },
        { title: 'Taze ürün tezgâhı', image: assets.globusCampaignFish, imageAlt: 'GLOBUS balık tezgâhında çalışan uzman', href: '/globus' },
        { title: 'Gastronomi', image: assets.globusCampaignGastronomy, imageAlt: 'GLOBUS gastronomisinde çalışan uzman', href: '/globus' },
      ],
    },
  },
};
